/**
 * Algemene Instellingen
 */

import { useState, useEffect } from 'react';
import { bedrijfsgegevensService, BedrijfsgegevensUpdate } from '../../services/bedrijfsgegevens.service';
import Toast from '../../components/Toast';
import './Settings.css';
import '../relations/Relations.css';

type Documentsoort = 'werkorder' | 'factuur' | 'offerte' | 'inkoop';

const DOCUMENTSOORTEN: { sleutel: Documentsoort; titel: string; plaatshouder: string }[] = [
  { sleutel: 'werkorder', titel: 'Werkorder', plaatshouder: 'WO' },
  { sleutel: 'factuur', titel: 'Verkoop Factuur', plaatshouder: 'F' },
  { sleutel: 'offerte', titel: 'Kostenraming', plaatshouder: 'KR' },
  { sleutel: 'inkoop', titel: 'Inkoop Factuur', plaatshouder: 'INK' },
];

const AlgemeneInstellingen = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [uurloonInkoop, setUurloonInkoop] = useState<number>(0);
  const [uurloonVerkoop, setUurloonVerkoop] = useState<number>(0);


  const [formData, setFormData] = useState<BedrijfsgegevensUpdate>({
    // Werkorder nummering
    werkorder_prefix: 'WO',
    werkorder_gebruik_jaar: true,
    werkorder_jaar_formaat: 'YYYY',
    werkorder_volgnummer_lengte: 4,
    werkorder_volgnummer_start: 1,
    werkorder_scheiding: '_',
    
    // Factuur nummering
    factuur_prefix: 'F',
    factuur_gebruik_jaar: true,
    factuur_jaar_formaat: 'YYYY',
    factuur_volgnummer_lengte: 4,
    factuur_volgnummer_start: 1,
    factuur_scheiding: '_',
    
    // Offerte nummering
    offerte_prefix: 'KR',
    offerte_gebruik_jaar: true,
    offerte_jaar_formaat: 'YYYY',
    offerte_volgnummer_lengte: 4,
    offerte_volgnummer_start: 1,
    offerte_scheiding: '_',
    
    // Inkoop nummering
    inkoop_prefix: 'INK',
    inkoop_gebruik_jaar: true,
    inkoop_jaar_formaat: 'YYYY',
    inkoop_volgnummer_lengte: 4,
    inkoop_volgnummer_start: 1,
    inkoop_scheiding: '_',
  });

  useEffect(() => {
    loadInstellingen();
  }, []);

  const loadInstellingen = async () => {
    try {
      setLoading(true);
      const data = await bedrijfsgegevensService.get();
      setUurloonInkoop(data.uurloon_inkoop || 0);
      setUurloonVerkoop(data.uurloon_verkoop || 0);
      setFormData({
        // Werkorder nummering
        werkorder_prefix: data.werkorder_prefix || 'WO',
        werkorder_gebruik_jaar: data.werkorder_gebruik_jaar ?? true,
        werkorder_jaar_formaat: data.werkorder_jaar_formaat || 'YYYY',
        werkorder_volgnummer_lengte: data.werkorder_volgnummer_lengte || 4,
        werkorder_volgnummer_start: data.werkorder_volgnummer_start || 1,
        werkorder_scheiding: data.werkorder_scheiding || '_',
        
        // Factuur nummering
        factuur_prefix: data.factuur_prefix || 'F',
        factuur_gebruik_jaar: data.factuur_gebruik_jaar ?? true,
        factuur_jaar_formaat: data.factuur_jaar_formaat || 'YYYY',
        factuur_volgnummer_lengte: data.factuur_volgnummer_lengte || 4,
        factuur_volgnummer_start: data.factuur_volgnummer_start || 1,
        factuur_scheiding: data.factuur_scheiding || '_',
        
        // Offerte nummering
        offerte_prefix: data.offerte_prefix || 'KR',
        offerte_gebruik_jaar: data.offerte_gebruik_jaar ?? true,
        offerte_jaar_formaat: data.offerte_jaar_formaat || 'YYYY',
        offerte_volgnummer_lengte: data.offerte_volgnummer_lengte || 4,
        offerte_volgnummer_start: data.offerte_volgnummer_start || 1,
        offerte_scheiding: data.offerte_scheiding || '_',
        
        // Inkoop nummering
        inkoop_prefix: data.inkoop_prefix || 'INK',
        inkoop_gebruik_jaar: data.inkoop_gebruik_jaar ?? true,
        inkoop_jaar_formaat: data.inkoop_jaar_formaat || 'YYYY',
        inkoop_volgnummer_lengte: data.inkoop_volgnummer_lengte || 4,
        inkoop_volgnummer_start: data.inkoop_volgnummer_start || 1,
        inkoop_scheiding: data.inkoop_scheiding || '_',
      });
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen instellingen');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      setLoading(true);
      await bedrijfsgegevensService.update({
        uurloon_inkoop: uurloonInkoop,
        uurloon_verkoop: uurloonVerkoop,
        ...formData,
      });
      setSuccess('Instellingen succesvol opgeslagen!');
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het opslaan');
    } finally {
      setLoading(false);
    }
  };

  if (loading && uurloonInkoop === 0 && uurloonVerkoop === 0) {
    return <div className="loading">Laden...</div>;
  }

  // Voorbeeldjaar in de preview: YY = 26, YYQ = 26 + kwartaalnummer, anders 2026
  const jaarVoorbeeld = (formaat?: string) => {
    const nu = new Date();
    const kort = String(nu.getFullYear()).slice(-2);
    if (formaat === 'YY') return kort;
    if (formaat === 'YYQ') return kort + String(Math.floor(nu.getMonth() / 3) + 1);
    return String(nu.getFullYear());
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Algemene Instellingen</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />
      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />

      <form onSubmit={handleSubmit} className="form-container">
        {/* Tarieven Instellingen */}
        <section className="form-section">
          <h2>Tarieven Instellingen</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="uurloon_inkoop">Uurloon Inkoop (€)</label>
              <input
                type="number"
                id="uurloon_inkoop"
                name="uurloon_inkoop"
                value={uurloonInkoop / 100}
                onChange={(e) => {
                  const euros = parseFloat(e.target.value) || 0;
                  setUurloonInkoop(Math.round(euros * 100));
                }}
                step="0.01"
                min="0"
                placeholder="50.00"
              />
              <small>Prijs per uur voor inkoop/kostprijs</small>
            </div>

            <div className="form-group">
              <label htmlFor="uurloon_verkoop">Uurloon Verkoop (€)</label>
              <input
                type="number"
                id="uurloon_verkoop"
                name="uurloon_verkoop"
                value={uurloonVerkoop / 100}
                onChange={(e) => {
                  const euros = parseFloat(e.target.value) || 0;
                  setUurloonVerkoop(Math.round(euros * 100));
                }}
                step="0.01"
                min="0"
                placeholder="75.00"
              />
              <small>Prijs per uur voor verkoop aan klanten</small>
            </div>
          </div>
        </section>


        {/* Document Nummering - per documentsoort apart in te stellen */}
        <section className="form-section">
          <h2>Document Nummering</h2>
          <p className="section-description">
            Elke documentsoort heeft zijn eigen prefix, scheidingsteken, jaarformaat en volgnummer.
          </p>

          {DOCUMENTSOORTEN.map(({ sleutel, titel, plaatshouder }) => {
            const velden = formData as Record<string, any>;
            const prefix: string = velden[`${sleutel}_prefix`] || '';
            const scheiding: string = velden[`${sleutel}_scheiding`] || '';
            const gebruikJaar: boolean = velden[`${sleutel}_gebruik_jaar`];
            const jaarFormaat: string = velden[`${sleutel}_jaar_formaat`];
            const lengte: number = velden[`${sleutel}_volgnummer_lengte`] || 4;
            const startNummer: number = velden[`${sleutel}_volgnummer_start`] || 1;

            return (
              <div key={sleutel}>
                <h3>{titel}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor={`${sleutel}_prefix`}>Prefix</label>
                    <input
                      id={`${sleutel}_prefix`}
                      name={`${sleutel}_prefix`}
                      type="text"
                      value={prefix}
                      onChange={handleChange}
                      maxLength={10}
                      placeholder={plaatshouder}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`${sleutel}_scheiding`}>Scheidingsteken</label>
                    <input
                      id={`${sleutel}_scheiding`}
                      name={`${sleutel}_scheiding`}
                      type="text"
                      value={scheiding}
                      onChange={handleChange}
                      maxLength={5}
                      placeholder="_"
                    />
                    <small className="form-help">Teken tussen prefix en jaar</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name={`${sleutel}_gebruik_jaar`}
                        checked={gebruikJaar}
                        onChange={handleChange}
                      />
                      Jaar gebruiken in nummer
                    </label>
                  </div>

                  {gebruikJaar && (
                    <div className="form-group">
                      <label htmlFor={`${sleutel}_jaar_formaat`}>Jaar Formaat</label>
                      <select
                        id={`${sleutel}_jaar_formaat`}
                        name={`${sleutel}_jaar_formaat`}
                        value={jaarFormaat}
                        onChange={handleChange}
                      >
                        <option value="YYYY">Volledig jaar (YYYY)</option>
                        <option value="YY">Kort jaar (YY)</option>
                        <option value="YYQ">Kort jaar + kwartaal (YYQ)</option>
                      </select>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor={`${sleutel}_volgnummer_lengte`}>Volgnummer lengte</label>
                    <input
                      id={`${sleutel}_volgnummer_lengte`}
                      name={`${sleutel}_volgnummer_lengte`}
                      type="number"
                      min="1"
                      max="8"
                      value={lengte}
                      onChange={handleChange}
                    />
                    <small className="form-help">Aantal cijfers voor volgnummer (1-8)</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`${sleutel}_volgnummer_start`}>Start nummer</label>
                    <input
                      id={`${sleutel}_volgnummer_start`}
                      name={`${sleutel}_volgnummer_start`}
                      type="number"
                      min="0"
                      value={startNummer}
                      onChange={handleChange}
                    />
                    <small className="form-help">Vanaf welk nummer beginnen</small>
                  </div>
                </div>

                <div className="form-preview">
                  <span className="preview-text">
                    {prefix}
                    {gebruikJaar && (
                      <>
                        {scheiding}
                        {jaarVoorbeeld(jaarFormaat)}
                      </>
                    )}
                    {String(startNummer).padStart(lengte, '0')}
                  </span>
                </div>
              </div>
            );
          })}
        </section>

        <div className="form-actions">
          <button
            type="submit"
            className="btn-lg btn-success"
            disabled={loading}
          >
            {loading ? 'Bezig met opslaan...' : 'Opslaan'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AlgemeneInstellingen;